import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { TrendingUp, TrendingDown, Minus } from 'lucide-react';

interface WeekStats {
  totalViews: number;
  uniqueSessions: number;
  avgSessionDuration: number;
  topPage: { page: string; views: number } | null;
}

interface TrendData {
  current: WeekStats;
  previous: WeekStats;
}

export const AnalyticsSummaryWidget = () => {
  const [data, setData] = useState<TrendData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAnalytics = async () => {
      try {
        const now = new Date();
        const last7Days = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        const previous14Days = new Date(now.getTime() - 14 * 24 * 60 * 60 * 1000);

        // Fetch last 14 days of data
        const { data: views, error } = await supabase
          .from('page_views')
          .select('*')
          .gte('created_at', previous14Days.toISOString())
          .order('created_at', { ascending: false });

        if (error) throw error;

        if (!views || views.length === 0) {
          setData({
            current: { totalViews: 0, uniqueSessions: 0, avgSessionDuration: 0, topPage: null },
            previous: { totalViews: 0, uniqueSessions: 0, avgSessionDuration: 0, topPage: null }
          });
          return;
        }

        // Split data into current week and previous week
        const currentWeekViews = views.filter(v => new Date(v.created_at) >= last7Days);
        const previousWeekViews = views.filter(v => new Date(v.created_at) < last7Days);

        const calculateStats = (weekViews: typeof views): WeekStats => {
          const totalViews = weekViews.length;
          const uniqueSessions = new Set(weekViews.map(v => v.session_id)).size;

          // Calculate top page
          const pageCounts = weekViews.reduce((acc, view) => {
            acc[view.page_path] = (acc[view.page_path] || 0) + 1;
            return acc;
          }, {} as Record<string, number>);

          const topPageEntry = Object.entries(pageCounts)
            .sort(([, a], [, b]) => b - a)[0];
          
          const topPage = topPageEntry 
            ? { page: topPageEntry[0], views: topPageEntry[1] }
            : null;

          // Calculate average session duration
          const sessionTimes: Record<string, number[]> = {};
          weekViews.forEach(view => {
            if (!sessionTimes[view.session_id]) {
              sessionTimes[view.session_id] = [];
            }
            sessionTimes[view.session_id].push(new Date(view.created_at).getTime());
          });

          const durations = Object.values(sessionTimes).map(times => {
            if (times.length < 2) return 0;
            return Math.max(...times) - Math.min(...times);
          });

          const avgSessionDuration = durations.length > 0
            ? durations.reduce((a, b) => a + b, 0) / durations.length / 1000 / 60
            : 0;

          return {
            totalViews,
            uniqueSessions,
            avgSessionDuration: Math.round(avgSessionDuration * 10) / 10,
            topPage
          };
        };

        setData({
          current: calculateStats(currentWeekViews),
          previous: calculateStats(previousWeekViews)
        });
      } catch (error) {
        console.error('Failed to fetch analytics:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchAnalytics();
  }, []);

  const getTrendIcon = (current: number, previous: number) => {
    if (previous === 0) return <Minus className="h-4 w-4 text-muted-foreground" />;
    if (current > previous) return <TrendingUp className="h-4 w-4 text-green-600" />;
    if (current < previous) return <TrendingDown className="h-4 w-4 text-red-600" />;
    return <Minus className="h-4 w-4 text-muted-foreground" />;
  };

  const getTrendPercentage = (current: number, previous: number) => {
    if (previous === 0) return null;
    const change = ((current - previous) / previous) * 100;
    return `${change > 0 ? '+' : ''}${change.toFixed(0)}%`;
  };

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Analytics Summary</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-sm text-muted-foreground">Lädt...</div>
        </CardContent>
      </Card>
    );
  }

  if (!data) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Analytics Summary</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-sm text-muted-foreground">Keine Daten verfügbar</div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Analytics Summary</CardTitle>
        <p className="text-sm text-muted-foreground">Letzte 7 Tage • Vergleich zur Vorwoche</p>
      </CardHeader>
      <CardContent>
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          <div className="space-y-2">
            <div className="text-sm font-medium text-muted-foreground">Seitenaufrufe</div>
            <div className="flex items-baseline gap-2">
              <div className="text-2xl font-bold">{data.current.totalViews}</div>
              {getTrendIcon(data.current.totalViews, data.previous.totalViews)}
              {getTrendPercentage(data.current.totalViews, data.previous.totalViews) && (
                <span className="text-xs text-muted-foreground">
                  {getTrendPercentage(data.current.totalViews, data.previous.totalViews)}
                </span>
              )}
            </div>
          </div>

          <div className="space-y-2">
            <div className="text-sm font-medium text-muted-foreground">Eindeutige Sessions</div>
            <div className="flex items-baseline gap-2">
              <div className="text-2xl font-bold">{data.current.uniqueSessions}</div>
              {getTrendIcon(data.current.uniqueSessions, data.previous.uniqueSessions)}
              {getTrendPercentage(data.current.uniqueSessions, data.previous.uniqueSessions) && (
                <span className="text-xs text-muted-foreground">
                  {getTrendPercentage(data.current.uniqueSessions, data.previous.uniqueSessions)}
                </span>
              )}
            </div>
          </div>

          <div className="space-y-2">
            <div className="text-sm font-medium text-muted-foreground">Ø Sitzungsdauer</div>
            <div className="flex items-baseline gap-2">
              <div className="text-2xl font-bold">{data.current.avgSessionDuration}m</div>
              {getTrendIcon(data.current.avgSessionDuration, data.previous.avgSessionDuration)}
              {getTrendPercentage(data.current.avgSessionDuration, data.previous.avgSessionDuration) && (
                <span className="text-xs text-muted-foreground">
                  {getTrendPercentage(data.current.avgSessionDuration, data.previous.avgSessionDuration)}
                </span>
              )}
            </div>
          </div>

          <div className="space-y-2">
            <div className="text-sm font-medium text-muted-foreground">Meistbesuchte Seite</div>
            <div className="text-lg font-semibold truncate">
              {data.current.topPage ? data.current.topPage.page : '-'}
            </div>
            {data.current.topPage && (
              <div className="text-xs text-muted-foreground">
                {data.current.topPage.views} Aufrufe
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
